// src/components/CartPage.jsx

import { useCart, cartLineRefForUpdate } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import BackButton from "./BackButton";
import visa from "../images/visa.png";
import mastercard from "../images/mastercard.jpg";
import cash from "../images/cash.png";
export default function CartPage() {
  const { cartItems, removeFromCart, updateQuantity, totalPrice } = useCart();
  const navigate = useNavigate();

  // ✅ Utility to safely format price
  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  if (cartItems.length === 0) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-semibold text-gray-800">
          Your cart is empty
        </h2>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-10">
      <BackButton />

      <div className="flex flex-col lg:flex-row gap-10">
        {/* Left: Products */}
        <div className="flex-1">
          {cartItems.map((item, idx) => (
            <div
              key={item.cartLineUid ?? `${idx}-${item.id}-${item.size ?? ""}`}
              className="flex gap-4 mb-8"
            >
              {/* Image */}
              <img
                src={item.image_uri || item.image}
                alt={item.name}
                className="w-32 h-40 object-cover border"
              />

              {/* Details */}
              <div className="flex-1">
                <div className="font-medium text-gray-800">
                  {item.name || item.title}
                </div>
                {/* ✅ Use formatPrice() */}
                <div className="text-blue-600 font-semibold">
                  USD {formatPrice(item.price)}
                </div>

                <div className="text-sm text-gray-600 mt-2 space-y-1">
                  <div>
                    <strong>Size:</strong> {item.size || "N/A"}
                  </div>
                  <div>
                    <strong>Quantity:</strong> {item.quantity}
                  </div>
                  {/* ✅ Use formatPrice() for total */}
                  <div>
                    <strong>Item Total:</strong>{" "}
                    <span className="font-semibold">
                      USD {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center gap-2 mt-3 border border-gray-300 rounded-md p-1">
                  <button
                    onClick={() =>
                      updateQuantity(
                        cartLineRefForUpdate(item),
                        item.quantity - 1
                      )
                    }
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900"
                  >
                    −
                  </button>
                  <span className="w-8 text-center">{item.quantity}</span>
                  <button
                    onClick={() =>
                      updateQuantity(
                        cartLineRefForUpdate(item),
                        item.quantity + 1
                      )
                    }
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900"
                  >
                    +
                  </button>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeFromCart(cartLineRefForUpdate(item))}
                  className="mt-2 text-gray-500 hover:text-blue-600 text-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 inline"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Right: Summary */}
        <div className="w-full lg:w-80">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <div className="mb-6">
              <div className="flex justify-between text-sm font-medium mb-2">
                <span>DISCOUNTS</span>
                <button className="text-blue-600 hover:underline text-sm">
                  ADD
                </button>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Order value</span>
                  <span className="font-semibold">
                    USD {formatPrice(totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Estimated delivery fee</span>
                  <span className="font-semibold">Free</span>
                </div>
              </div>

              <div className="border-t mt-4 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>TOTAL</span>
                  <span>USD {formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full py-3 bg-black text-white font-semibold rounded-md mb-4 hover:bg-gray-800 transition-colors"
            >
              CONTINUE TO CHECKOUT
            </button>
            {/* 
            <button
              onClick={() => navigate('/account')}
              className="w-full py-3 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 transition-colors"
            >
              SIGN IN
            </button> */}

            <div className="mt-6 flex gap-4">
              {/* ✅ Fixed: Removed extra spaces in URLs */}
              <img src={visa} alt="Visa" className="h-6" />
              <img src={mastercard} alt="Mastercard" className="h-6" />
              <img src={cash} alt="Cash on Delivery" className="h-6" />
            </div>

            <p className="text-sm text-gray-600 mt-6">
              Prices and delivery costs are not confirmed until you've reached
              the checkout.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              15 days free returns. Read more about{" "}
              <a href="#" className="text-blue-600 hover:underline">
                return and refund policy
              </a>
              .
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Need help? Please contact{" "}
              <a href="#" className="text-blue-600 hover:underline">
                Customer Support
              </a>
              .<br />
              Customers would receive an SMS/WhatsApp notifications regarding
              deliveries on the registeblue phone number
            </p>

            <a
              href="#"
              className="block text-sm text-blue-600 hover:underline mt-6"
            >
              DELIVERY AND RETURN OPTIONS
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
